import type { useDisclosureWithContext } from '@talos/kyoko';
import { ButtonVariants, Dialog, Text } from '@talos/kyoko';
import { useCallback } from 'react';
import type { LPSettlementBatchMessageData } from './generated/lpSettlementTypes';
import { useUngroupBatchMutation } from './useLpSettlementApi';

export interface UnbatchDialogContext {
  settlementBatch: LPSettlementBatchMessageData;
  onClose?: () => void;
}

interface UnbatchConfirmationDialogProps extends Pick<
  ReturnType<typeof useDisclosureWithContext<UnbatchDialogContext>>,
  'isOpen' | 'close'
> {
  context: UnbatchDialogContext;
}

export function UnbatchConfirmationDialog({ context, ...dialog }: UnbatchConfirmationDialogProps) {
  const [ungroupBatch, { isLoading }] = useUngroupBatchMutation();

  const handleConfirm = useCallback(async () => {
    try {
      await ungroupBatch({ settlementBatch: context.settlementBatch }).unwrap();
      context.onClose?.();
      dialog.close();
    } catch {
      // Error handling is done by the mutation's onQueryStarted callback
    }
  }, [context, ungroupBatch, dialog]);

  return (
    <Dialog
      {...dialog}
      title="Unbatch"
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      variant={ButtonVariants.Negative}
      dataTestId="unbatch-confirmation-dialog"
      confirmLoading={isLoading}
      confirmDisabled={isLoading}
      preventClosing={isLoading}
      onConfirm={handleConfirm}
    >
      <Text>Are you sure you want to unbatch ID &apos;{context.settlementBatch.BatchID}&apos;?</Text>
    </Dialog>
  );
}
